/** Per-agent hash-chained audit explorer for write / read / chat / run actions. */
import { useCallback, useEffect, useState } from 'react';
import {
  DataTable,
  FilterBar,
  GlassButton,
  GlassInput,
  GlassSelect,
  StatusPill,
  useI18n,
  type DataTableColumn,
} from '@servicesai/ui';
import { admin } from '../../lib/api.js';
import { S } from '../../strings.js';
import { scrubModelIds } from '../../lib/aiBranding.js';

type AuditCategory = 'all' | 'write' | 'read' | 'chat' | 'run';

interface AgentAuditRow {
  id: number;
  ts: string;
  actor: Record<string, unknown>;
  action: string;
  subject: Record<string, unknown>;
  detail: Record<string, unknown>;
  this_hash: string;
  category: Exclude<AuditCategory, 'all'> | 'other';
}

const CATEGORY_STATES: Record<string, 'published' | 'in_review' | 'in_progress' | 'draft' | 'rejected'> = {
  write: 'in_review',
  read: 'in_progress',
  chat: 'published',
  run: 'draft',
  other: 'draft',
};

export function AgentAuditPanel({ slug }: { slug: string }) {
  const { l } = useI18n();
  const [items, setItems] = useState<AgentAuditRow[] | null>(null);
  const [nextBeforeId, setNextBeforeId] = useState<number | null>(null);
  const [category, setCategory] = useState<AuditCategory>('all');
  const [actionPrefix, setActionPrefix] = useState('');
  const [actorKind, setActorKind] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(
    async (mode: 'replace' | 'append' = 'replace') => {
      if (mode === 'replace') setLoading(true);
      else setLoadingMore(true);
      setError(null);
      try {
        const page = await admin().agentAudit(slug, {
          limit: 50,
          beforeId: mode === 'append' && nextBeforeId != null ? nextBeforeId : undefined,
          category,
          actionPrefix: actionPrefix.trim() || undefined,
          actorKind: actorKind || undefined,
        });
        const rows = page.items as AgentAuditRow[];
        setItems((prev) => (mode === 'append' && prev ? [...prev, ...rows] : rows));
        setNextBeforeId(page.nextBeforeId);
      } catch (loadError) {
        setError((loadError as Error).message);
        if (mode === 'replace') setItems([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [slug, category, actionPrefix, actorKind, nextBeforeId],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setNextBeforeId(null);
      void load('replace');
    }, 200);
    return () => window.clearTimeout(timer);
    // Intentionally omit `load` / `nextBeforeId` so filter changes reset pagination.
  }, [slug, category, actionPrefix, actorKind]);

  const columns: DataTableColumn<AgentAuditRow>[] = [
    {
      id: 'id',
      header: '#',
      sortable: true,
      value: (row) => row.id,
      render: (row) => <span className="font-mono font-bold">{row.id}</span>,
    },
    {
      id: 'time',
      header: l(S.agentAuditTime),
      sortable: true,
      value: (row) => row.ts,
      render: (row) => <time className="text-xs">{new Date(row.ts).toLocaleString()}</time>,
    },
    {
      id: 'category',
      header: l(S.agentAuditCategory),
      value: (row) => row.category,
      render: (row) => (
        <StatusPill
          state={CATEGORY_STATES[row.category] ?? 'draft'}
          label={row.category}
        />
      ),
    },
    {
      id: 'action',
      header: l(S.agentAuditAction),
      sortable: true,
      value: (row) => row.action,
      render: (row) => <code className="text-xs font-bold">{row.action}</code>,
    },
    {
      id: 'actor',
      header: l(S.agentAuditActor),
      value: (row) => String(row.actor.id ?? row.actor.kind ?? ''),
      render: (row) => (
        <div>
          <div className="font-semibold">{String(row.actor.kind ?? 'system')}</div>
          <div className="font-mono text-[10px] text-ink-faint">
            {String(row.actor.id ?? '—').slice(0, 18)}
          </div>
        </div>
      ),
    },
    {
      id: 'subject',
      header: l(S.agentAuditSubject),
      value: (row) => String(row.subject.id ?? ''),
      render: (row) => (
        <div>
          <div className="text-xs font-semibold">{String(row.subject.table ?? '—')}</div>
          <div className="font-mono text-[10px] text-ink-faint">
            {String(row.subject.id ?? '—').slice(0, 18)}
          </div>
        </div>
      ),
    },
    {
      id: 'detail',
      header: l(S.agentAuditDetail),
      value: (row) => row.id,
      render: (row) => (
        <GlassButton
          size="sm"
          variant={expandedId === row.id ? 'primary' : 'ghost'}
          onClick={() => setExpandedId((current) => (current === row.id ? null : row.id))}
        >
          {expandedId === row.id ? l(S.agentAuditHideDetail) : l(S.agentAuditShowDetail)}
        </GlassButton>
      ),
    },
  ];

  const expanded = items?.find((row) => row.id === expandedId) ?? null;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-bold text-ink">{l(S.agentAuditTitle)}</h2>
        <p className="mt-1 text-sm text-ink-soft">{l(S.agentAuditSubtitle)}</p>
      </div>

      {error && (
        <div className="aegov-alert alert-error" role="alert">
          <div className="alert-content">{error}</div>
        </div>
      )}

      <FilterBar summary={`${items?.length ?? 0} ${l(S.agentAuditEntries)}`}>
        {(['all', 'write', 'read', 'chat', 'run'] as const).map((value) => (
          <GlassButton
            key={value}
            size="sm"
            variant={category === value ? 'primary' : 'ghost'}
            onClick={() => setCategory(value)}
          >
            {l(
              value === 'all'
                ? S.agentAuditAll
                : value === 'write'
                  ? S.agentAuditWrite
                  : value === 'read'
                    ? S.agentAuditRead
                    : value === 'chat'
                      ? S.agentAuditChat
                      : S.agentAuditRun,
            )}
          </GlassButton>
        ))}
        <GlassInput
          value={actionPrefix}
          onChange={(event) => setActionPrefix(event.target.value)}
          placeholder={l(S.agentAuditActionPrefix)}
          className="w-full sm:w-56"
          aria-label={l(S.agentAuditActionPrefix)}
        />
        <GlassSelect
          value={actorKind}
          onChange={(event) => setActorKind(event.target.value)}
          aria-label={l(S.agentAuditActorKind)}
          className="w-full sm:w-40"
        >
          <option value="">{l(S.agentAuditAnyActor)}</option>
          <option value="staff">staff</option>
          <option value="engine">engine</option>
          <option value="system">system</option>
          <option value="citizen">citizen</option>
        </GlassSelect>
        <GlassButton size="sm" variant="ghost" onClick={() => void load('replace')} disabled={loading}>
          {l(S.refresh)}
        </GlassButton>
      </FilterBar>

      <DataTable
        rows={items ?? []}
        columns={columns}
        rowKey={(row) => String(row.id)}
        loading={loading && items === null}
        emptyTitle={l(S.agentAuditEmpty)}
        filename={`${slug}-agent-audit.csv`}
      />

      {expanded && (
        <div className="rounded-xl border border-aeblack-100 bg-white p-4">
          <div className="mb-2 flex items-center justify-between gap-2">
            <h3 className="text-sm font-bold">
              {l(S.agentAuditDetail)} #{expanded.id}
            </h3>
            <code className="max-w-[50%] truncate font-mono text-[10px] text-ink-faint" title={expanded.this_hash}>
              {expanded.this_hash}
            </code>
          </div>
          <pre className="overflow-x-auto rounded-lg bg-aeblack-50 p-3 text-[11px] leading-relaxed text-ink">
            {scrubModelIds(JSON.stringify(
              {
                actor: expanded.actor,
                action: expanded.action,
                subject: expanded.subject,
                detail: expanded.detail,
                category: expanded.category,
              },
              null,
              2,
            ))}
          </pre>
        </div>
      )}

      {nextBeforeId != null && (
        <div className="flex justify-center">
          <GlassButton
            variant="ghost"
            disabled={loadingMore}
            onClick={() => void load('append')}
          >
            {loadingMore ? l(S.running) : l(S.agentAuditLoadMore)}
          </GlassButton>
        </div>
      )}
    </div>
  );
}
